import { ComponentBase } from "../core/ComponentBase.js";

/**
 * TitleBarControls — renders native-looking minimize, maximize, and close buttons
 * specifically for frameless Electron windows.
 * 
 * It automatically hides itself if not running inside the Electron shell.
 */
export class TitleBarControls extends ComponentBase {
    static styles = `
        .tb-controls {
            display: flex;
            align-items: center;
            height: 100%;
            app-region: no-drag;
            -webkit-app-region: no-drag; /* Buttons must not be draggable */
        }
        .electron-only-titlebar-host {
            display: none !important;
        }
        body.is-electron .electron-only-titlebar-host {
            display: flex !important;
        }
        .tb-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 46px;
            height: 100%;
            border: none;
            background: transparent;
            color: var(--bs-body-color);
            cursor: pointer;
            transition: background-color 0.1s;
            app-region: no-drag;
            -webkit-app-region: no-drag;
        }
        .tb-btn:hover {
            background-color: rgba(128, 128, 128, 0.15);
        }
        .tb-btn.close:hover {
            background-color: #e81123;
            color: #fff;
        }
        .tb-icon {
            font-size: var(--ab-text-base);
        }
    `;

    constructor(config) {
        super(config);
        this.applyStyles();
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'tb-controls';
            this.container.style.minWidth = '100px';

            const isElectron = !!window.electronAPI;

            if (!isElectron) {
                this.container.style.display = 'none';
                return this.container;
            }
            document.body.classList.add('is-electron');
            this.container.style.display = 'flex';

            // Only render controls if running inside Electron
            if (isElectron) {
                // Settings Dropdown
                const settingsWrapper = document.createElement('div');
                settingsWrapper.className = 'dropdown h-100';

                const settingsBtn = document.createElement('button');
                settingsBtn.className = 'tb-btn';
                settingsBtn.setAttribute('data-bs-toggle', 'dropdown');
                settingsBtn.innerHTML = '<i class="bi bi-three-dots tb-icon"></i>';

                const dropdownMenu = document.createElement('ul');
                dropdownMenu.className = 'dropdown-menu dropdown-menu-end shadow rounded-3 text-sm';
                dropdownMenu.style.fontSize = '0.85rem';

                const trayToggleLi = document.createElement('li');
                const trayToggleA = document.createElement('a');
                trayToggleA.className = 'dropdown-item d-flex align-items-center gap-2';
                trayToggleA.href = '#';

                let trayEnabled = false;
                let autoStartEnabled = false;

                const updateLabels = () => {
                    trayToggleA.textContent = trayEnabled
                        ? 'Disable System Tray'
                        : 'Enable System Tray';

                    autoStartToggleA.textContent = autoStartEnabled
                        ? 'Disable Auto Start'
                        : 'Start with System';
                };

                const autoStartLi = document.createElement('li');
                const autoStartToggleA = document.createElement('a');
                autoStartToggleA.className = 'dropdown-item';
                autoStartToggleA.href = '#';

                const quitLi = document.createElement('li');
                const quitA = document.createElement('a');
                quitA.className = 'dropdown-item text-danger';
                quitA.href = '#';
                quitA.textContent = 'Quit Application';

                const separatorLi = document.createElement('li');
                separatorLi.innerHTML = '<hr class="dropdown-divider">';

                // Fetch initial config
                window.electronAPI.getConfig().then(cfg => {
                    trayEnabled = cfg.tray.enabled;
                    autoStartEnabled = cfg.autoStart.enabled;
                    updateLabels();
                });

                if (window.electronAPI.onConfigUpdated) {
                    window.electronAPI.onConfigUpdated((cfg) => {
                        trayEnabled = cfg.tray.enabled;
                        autoStartEnabled = cfg.autoStart.enabled;
                        updateLabels();
                    });
                }

                trayToggleA.onclick = (e) => {
                    e.preventDefault();
                    trayEnabled = !trayEnabled;
                    updateLabels();
                    window.electronAPI.toggleTray(trayEnabled);
                };

                autoStartToggleA.onclick = (e) => {
                    e.preventDefault();
                    autoStartEnabled = !autoStartEnabled;
                    updateLabels();
                    window.electronAPI.toggleAutoStart(autoStartEnabled);
                };

                quitA.onclick = (e) => {
                    e.preventDefault();
                    window.electronAPI.quit();
                };

                trayToggleLi.appendChild(trayToggleA);
                autoStartLi.appendChild(autoStartToggleA);
                quitLi.appendChild(quitA);

                dropdownMenu.appendChild(autoStartLi);
                dropdownMenu.appendChild(trayToggleLi);
                dropdownMenu.appendChild(separatorLi);
                dropdownMenu.appendChild(quitLi);

                settingsWrapper.appendChild(settingsBtn);
                settingsWrapper.appendChild(dropdownMenu);

                // Minimize
                const minBtn = document.createElement('button');
                minBtn.className = 'tb-btn';
                minBtn.innerHTML = '<i class="bi bi-dash tb-icon"></i>';
                minBtn.onclick = () => window.electronAPI.minimize();

                // Maximize/Restore
                const maxBtn = document.createElement('button');
                maxBtn.className = 'tb-btn';
                maxBtn.innerHTML = '<i class="bi bi-square tb-icon" style="font-size:var(--ab-text-sm);"></i>';
                maxBtn.onclick = () => window.electronAPI.maximize();

                // Close
                const closeBtn = document.createElement('button');
                closeBtn.className = 'tb-btn close';
                closeBtn.innerHTML = '<i class="bi bi-x-lg tb-icon"></i>';
                closeBtn.onclick = () => window.electronAPI.close();

                this.container.appendChild(settingsWrapper);
                this.container.appendChild(minBtn);
                this.container.appendChild(maxBtn);
                this.container.appendChild(closeBtn);
            }
        }
        return this.container;
    }
}
